/*!
 * tone-duty.js — a duty governor for tools that drive the device speaker with
 * sustained tones.
 *
 * WHY THIS EXISTS
 * ---------------
 * Music is a gentle load and a sine is a brutal one. Measured on the shipped
 * /speaker-cleaner phase table against real music normalised to full scale
 * (tests/scratch/excursion-bench.mjs, 2026-08-22):
 *
 *     crest factor   music 15.8 dB   ·   our phases 0.6-6.0 dB
 *     => at the same PEAK level a phase delivers ~35x the mean-square power,
 *        and voice-coil heating goes as mean square x time.
 *     diaphragm excursion (2nd-order model, checked at Fs 600/800/1200 Hz)
 *        our worst phase 2.3-2.5x the peak excursion of full-scale music.
 *
 * Expressing each job as "minutes of full-scale music that would heat the coil
 * as much":
 *
 *     water    0:25        11 music-minutes      fine
 *     full     1:49        54 music-minutes      fine, about one loud album
 *     deep     15 min     468 music-minutes      ~8 hours of loud music
 *     deep     60 min    1872 music-minutes      ~31 hours of loud music
 *     one phase on repeat  UNBOUNDED — it looped for ever with nothing to stop it
 *
 * The short jobs are not the problem. Repetition is, and it was unbounded.
 * Apple's own water eject is a burst of a few seconds; every competitor
 * measured tops out between 10 and 60 seconds. Nothing in the field runs an
 * hour of continuous tone, and there is no evidence it helps — only that it
 * heats.
 *
 * WHY A TOKEN BUCKET AND NOT A ROLLING WINDOW
 * -------------------------------------------
 * The requirement is "one long run is fine, the same run on a loop is not",
 * and a single rolling-window budget cannot say that: sized to admit the 109 s
 * job it permits ~18% duty for ever, and sized for 5% duty it refuses the 109 s
 * job outright. The first draft here was a rolling window and its own contract
 * test caught exactly that — the "full" job came back with a 30-minute wait.
 *
 * A bucket separates the two knobs. CAPACITY is what a person may spend at
 * once; REFILL is what the speaker may sustain. Fresh, the bucket covers
 * "water" plus "water & dust" back to back with margin; drained, everything
 * settles to the refill rate.
 *
 * WHAT THE NUMBERS BUY
 *     capacity 180 s   ~94 music-minutes in one go — about one loud album, and
 *                      set equal to maxBurst so the whole 12-phase cycle (161 s)
 *                      fits in one uninterrupted piece.
 *     refill 5%        an unattended hour: ~360 s of tone, ~187 music-minutes
 *                      instead of 3600 s and 1872. Ten times less heat.
 *     maxBurst 180 s   the largest chunk charged up front, clamped to the
 *                      capacity so a request can always eventually be met.
 *                      The CALLER must not schedule a longer unbroken stretch
 *                      than maxBurstSec(); the governor advises, it cannot
 *                      interrupt a tone that is already playing.
 *
 * Pauses are not merely the safe shape, they are the sensible one: water needs
 * time to re-pool at the grille between bursts, so repeated short ejects are
 * what the physical method wants anyway.
 *
 * USAGE
 *     var duty = ToneDuty.create();            // or .create({refill: 0.08})
 *     var waitMs = duty.waitBefore(25);        // want 25 s of tone
 *     if (waitMs > 0) { showResting(waitMs); } // ALWAYS tell the user
 *     duty.spent(actualSecondsPlayed);         // report what really happened
 *
 * The caller reports what it actually played, not what it asked for: a run the
 * user stopped after two seconds must not be charged twenty-five.
 *
 * A rest the interface does not explain is indistinguishable from a tool that
 * stopped working — which is a complaint this tool has already collected once.
 * waitBefore() therefore returns milliseconds, so the caller can count down.
 *
 * Classic script (ES5, no imports) — safe for @light() + the ES2019 guard.
 */
(function (root) {
    'use strict';

    var DEFAULTS = {
        // Seconds of tone available in one go from a full bucket.
        capacitySec: 180,
        // Long-run share of wall time that may be tone (bucket refill rate).
        refill: 0.05,
        // The longest uninterrupted stretch, whatever the bucket says.
        maxContinuousSec: 180
    };

    function now() {
        return (root.performance && root.performance.now) ? root.performance.now() : Date.now();
    }

    function create(opts) {
        var cfg = {
            capacitySec: (opts && opts.capacitySec) || DEFAULTS.capacitySec,
            refill: (opts && opts.refill) || DEFAULTS.refill,
            maxContinuousSec: (opts && opts.maxContinuousSec) || DEFAULTS.maxContinuousSec
        };
        // INVARIANT: a burst can never be longer than the bucket can hold.
        // Without this, waitBefore() quotes a wait for tokens the bucket is
        // incapable of accumulating, the caller rests, asks again, gets the
        // same quote — and rests FOR EVER. The tool never plays a note and
        // looks broken while insisting it is cooling. Caught by the wiring
        // test on /speaker-cleaner, which shrank the bucket below the burst.
        if (cfg.maxContinuousSec > cfg.capacitySec) cfg.maxContinuousSec = cfg.capacitySec;
        var tokens = cfg.capacitySec;      // seconds of tone in hand
        var last = now();

        function refill() {
            var t = now();
            // A clock that goes BACKWARDS must not mint or destroy budget.
            // performance.now() is monotonic, but the Date.now() fallback is
            // not: a system clock adjustment, or a caller that hands us a fake
            // clock, made the elapsed term negative and the bucket went deeply
            // negative in one call — found by this module's own contract test,
            // which reset its fake clock between cases.
            var dt = Math.max(0, t - last);
            tokens = Math.min(cfg.capacitySec, tokens + dt / 1000 * cfg.refill);
            last = t;
        }

        /**
         * How long the caller must rest before it may play `want` seconds.
         * @returns {number} milliseconds; 0 means go now.
         */
        function waitBefore(want) {
            refill();
            // Never quote a wait for more than one burst is allowed to be: the
            // caller is expected to split, and quoting an unreachable wait
            // would strand it.
            var need = Math.min(want, cfg.maxContinuousSec);
            if (tokens >= need) return 0;
            return Math.ceil((need - tokens) / cfg.refill * 1000);
        }

        /** Report tone actually delivered. */
        function spent(seconds) {
            if (!(seconds > 0)) return;
            refill();
            tokens = Math.max(-cfg.capacitySec, tokens - seconds);
        }

        /** The longest single stretch a caller may schedule. */
        function maxBurstSec() {
            return cfg.maxContinuousSec;
        }

        /** Diagnostics for telemetry — never an input to a decision. */
        function state() {
            refill();
            return {
                tokensSec: Math.round(tokens),
                capacitySec: cfg.capacitySec,
                refill: cfg.refill
            };
        }

        function reset() { tokens = cfg.capacitySec; last = now(); }

        return {
            waitBefore: waitBefore,
            spent: spent,
            maxBurstSec: maxBurstSec,
            state: state,
            reset: reset,
            config: cfg
        };
    }

    root.ToneDuty = { create: create, DEFAULTS: DEFAULTS };
})(typeof window !== 'undefined' ? window : this);
